import React, { type ReactElement, useEffect, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { CarouselContext } from './context/CarouselContext';
import { useCarouselDots } from './hooks/useCarouselDots';
import { useCarouselNavigation } from './hooks/useCarouselNavigation';
import { useCarouselVisibility } from './hooks/useCarouselVisibility';
import * as styles from './ScrollingCarousel.styles';
import type { ScrollingCarouselProps } from './ScrollingCarousel.types';

export const ScrollingCarousel = ({
  className,
  children,
  showButtons = true,
  showDots = false,
  dotsColor = 'light',
  numberOfDots = 5,
  buttonsPosition = 'left-right',
  customLeftButton,
  infiniteScroll = false,
  customBackIcon = null,
  customNextIcon = null,
  showNavigationOnHover = false,
  id,
  current,
  itemWidth,
  gap,
  ariaLabel = 'Scrolling carousel',
}: ScrollingCarouselProps) => {
  const carousel = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  // Manage child visibility state
  const {
    state,
    carouselContextApi,
    firstVisibleIndex,
    lastVisibleIndex,
    firstItemIsVisible,
    lastItemIsVisible,
    anyItemsVisible,
  } = useCarouselVisibility(carousel);

  // Navigation handlers
  const { next, back, goTo } = useCarouselNavigation({
    carousel,
    id,
    firstItemIsVisible,
    lastItemIsVisible,
    firstVisibleIndex,
    lastVisibleIndex,
    infiniteScroll,
    childVisibilityLength: state.childVisibility.length,
  });

  // Handle controlled current prop to scroll to specific item
  useEffect(() => {
    if (carousel.current && typeof current === 'number' && current >= 0) {
      const childrenArray = Array.from(carousel.current.children);
      const selectedItem = childrenArray[current] as HTMLElement;
      if (selectedItem) {
        const selectedItemOffset =
          selectedItem.offsetLeft + selectedItem.offsetWidth / 2;
        const carouselCenter = carousel.current.offsetWidth / 2;
        const scrollLeft = selectedItemOffset - carouselCenter;

        carousel.current.scroll({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [current]);

  // Add an index prop to all the steps so they know how to tell us which step they are upon visibility change
  // Also pass itemWidth to each step for consistent sizing
  const childrenWithIndex = React.Children.map(
    children as ReactElement[],
    (child: ReactElement, index: number) =>
      React.cloneElement(child, { index, itemWidth })
  );

  // Dots positioning and visibility logic
  const { dotOffset, dotDistances, dotWidth } = useCarouselDots({
    childVisibility: state.childVisibility,
    firstVisibleIndex,
    lastVisibleIndex,
    anyItemsVisible,
    numberOfDots,
  });

  const hasMultipleChildren = state.childVisibility.length > 1;
  const shouldShowNavigation = showNavigationOnHover ? isHovering : true;

  return (
    <div
      css={styles.carouselRoot}
      className={className}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        css={styles.carousel(gap)}
        ref={carousel}
        data-testid="scrolling-carousel-carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      >
        <CarouselContext.Provider value={carouselContextApi}>
          {childrenWithIndex}
        </CarouselContext.Provider>
      </div>

      {hasMultipleChildren && shouldShowNavigation && (
        <div css={styles.controls(buttonsPosition)}>
          {showButtons && (!firstItemIsVisible || infiniteScroll) && (
            <button
              aria-label="Previous"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                back(e);
              }}
              css={
                customLeftButton
                  ? styles.customButtonLeft(buttonsPosition)
                  : styles.buttonLeft(buttonsPosition)
              }
              data-testid={`scrolling-carousel-button-back-${id}`}
            >
              {customBackIcon ? (
                customBackIcon
              ) : (
                <span css={styles.iconWrapper}>
                  <Icon variant="AngleLeft" css={styles.icon} />
                </span>
              )}
            </button>
          )}

          {showButtons && (!lastItemIsVisible || infiniteScroll) && (
            <button
              aria-label="Next"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                next(e);
              }}
              css={styles.buttonRight(buttonsPosition)}
              data-testid={`scrolling-carousel-button-next-${id}`}
            >
              {customNextIcon ? (
                customNextIcon
              ) : (
                <span css={styles.iconWrapper}>
                  <Icon variant="AngleRight" css={styles.icon} />
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {showDots && (
        <div css={styles.dots}>
          <div
            css={styles.dotsInner}
            style={{ maxWidth: `${dotWidth * numberOfDots}px` }}
          >
            {state.childVisibility.map((childVisibility, i) => {
              const distance = dotDistances[i] ?? 0;
              return (
                <button
                  type="button"
                  onClick={(e) => goTo(e, i)}
                  css={[
                    styles.dot(dotsColor),
                    distance === 2 && styles.dotDistance2,
                    distance === 3 && styles.dotDistance3,
                    distance > 3 && styles.dotDistanceGreaterThan3,
                    childVisibility && styles.dotVisible,
                  ]}
                  style={{
                    width: `${dotWidth}px`,
                    height: `${dotWidth}px`,
                    transform: `translateX(-${dotOffset.current}px)`,
                  }}
                  key={`step-${i + 1}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

ScrollingCarousel.displayName = 'ScrollingCarousel';
